/* Harper & Stone — the shared motion layer.
 *
 * Two things on this site used to change state with no transition at all:
 * every <details> snapped open and shut, and every piece of content that was
 * replaced in place — a filtered grid, a re-rendered cart, a variant's
 * photograph — was simply there on the next frame. Hover and reveal were
 * already eased, so these were the moments that made the site feel abrupt.
 *
 * Both are handled here, once, rather than by each component that needs
 * them. One delegated listener covers every accordion on every page,
 * including ones that arrive later through the Section Rendering API, so a
 * new <details> is smooth without anyone remembering to wire it up.
 *
 * Nothing here is load-bearing. Without this file, or with reduced motion
 * on, every control behaves exactly as the platform makes it behave.
 */

const REDUCED = matchMedia('(prefers-reduced-motion: reduce)');
const EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';
const DURATION = 380;

/* ---- Accordions -------------------------------------------------------- */

const running = new WeakMap();

/* The height of a <details> when closed: its summary plus its own vertical
 * padding and borders. Computed rather than measured by toggling `open`,
 * because toggling fires `toggle` events that other scripts listen for. */
function closedHeight(details, summary) {
  const cs = getComputedStyle(details);
  const ss = getComputedStyle(summary);
  return (
    summary.offsetHeight +
    parseFloat(ss.marginTop) +
    parseFloat(ss.marginBottom) +
    parseFloat(cs.paddingTop) +
    parseFloat(cs.paddingBottom) +
    parseFloat(cs.borderTopWidth) +
    parseFloat(cs.borderBottomWidth)
  );
}

function finish(details) {
  details.style.height = '';
  details.style.overflow = '';
  /* Cleared here, not at the click: the flag has to outlive the animation so
   * a second click mid-flight can tell which way the first one was going. */
  delete details.dataset.motion;
  running.delete(details);
}

function animate(details, from, to, done) {
  running.get(details)?.cancel();
  details.style.overflow = 'hidden';

  const animation = details.animate(
    { height: [`${from}px`, `${to}px`] },
    { duration: DURATION, easing: EASE },
  );
  running.set(details, animation);

  animation.onfinish = () => {
    done?.();
    finish(details);
  };
  /* A cancel means a newer animation took over; it owns the cleanup. */
  animation.oncancel = () => {};
}

function open(details, summary) {
  const from = details.offsetHeight;
  /* Pin the height before opening so the content does not flash at full
   * size for a frame, then read how tall the open state wants to be. */
  details.style.height = `${from}px`;
  details.style.overflow = 'hidden';
  details.open = true;

  const borders = details.offsetHeight - details.clientHeight;
  const to = details.scrollHeight + borders;
  animate(details, from, to);
  summary.setAttribute('aria-expanded', 'true');
}

function close(details, summary) {
  const from = details.offsetHeight;
  const to = closedHeight(details, summary);
  /* `open` stays true until the last frame — removing it first would hide
   * the content and leave nothing to animate. */
  animate(details, from, to, () => {
    details.open = false;
  });
  summary.setAttribute('aria-expanded', 'false');
}

function canAnimate(details) {
  return (
    !REDUCED.matches &&
    typeof details.animate === 'function' &&
    !details.hasAttribute('data-no-motion')
  );
}

/* One listener for the whole document. Enter and Space on a focused summary
 * arrive as click events too, so the keyboard takes the same path. */
document.addEventListener('click', (event) => {
  const summary = event.target.closest?.('summary');
  if (!summary) return;

  const details = summary.parentElement;
  if (!details || details.tagName !== 'DETAILS' || !canAnimate(details)) return;

  /* A link or a button inside a summary keeps its own behaviour. */
  const inner = event.target.closest('a, button, input, select, label');
  if (inner && inner !== summary && summary.contains(inner)) return;

  event.preventDefault();

  /* Mid-animation, `open` is still true while closing — so the direction is
   * read from what the shopper last asked for, not from the attribute. */
  const closing = details.dataset.motion === 'closing';
  if (details.open && !closing) {
    details.dataset.motion = 'closing';
    close(details, summary);
  } else {
    details.dataset.motion = 'opening';
    open(details, summary);
  }
});

/* Scripts that close a <details> themselves — the filter drawer keeps one
 * group open at a time — can ask for the smooth path instead of setting
 * `open = false`, which snaps. The event is cancelable: a caller that finds
 * it was not handled falls back to the instant close, so nothing depends on
 * this file having loaded.
 *
 *   const handled = !el.dispatchEvent(
 *     new CustomEvent('hs:details-close', { bubbles: true, cancelable: true }));
 *   if (!handled) el.open = false;
 */
document.addEventListener('hs:details-close', (event) => {
  const details = event.target;
  if (details?.tagName !== 'DETAILS' || !details.open || !canAnimate(details)) return;
  const summary = details.querySelector(':scope > summary');
  if (!summary) return;

  event.preventDefault();
  details.dataset.motion = 'closing';
  close(details, summary);
});

/* ---- Content swaps ----------------------------------------------------- */

/* Fade a region out, run the change, fade it back in.
 *
 * Used wherever the page replaces content in place. The fade-out is short —
 * it has to finish before the new content can appear, so it is on the
 * critical path of the interaction — and the fade-in takes its time.
 *
 * It resolves even if the transition never fires (a hidden element, a
 * background tab), because a swap that waits forever for an animation is a
 * page that stopped working for the sake of looking good.
 */
const OUT = 160;

async function swap(region, change) {
  if (!region || REDUCED.matches) {
    await change();
    return;
  }

  region.dataset.swap = 'out';
  await new Promise((resolve) => setTimeout(resolve, OUT));

  try {
    await change();
  } finally {
    /* Two frames: one for the new content to lay out at opacity 0, one for
     * the transition to have a starting value to move from. */
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        region.dataset.swap = 'in';
        setTimeout(() => delete region.dataset.swap, 520);
      }),
    );
  }
}

/* ---- Reveal on arrival ------------------------------------------------- */

/* Sections arrive, rather than being already there.
 *
 * This is the difference the client was describing when they said everything
 * "comes very fast" and asked for a journey. Nothing on the page moved as it
 * was scrolled to: a band was simply present the instant its pixels entered
 * the viewport, so a long page read as a list of things rather than a
 * sequence. The reference sites all do the same quiet thing — content lifts
 * a few pixels and fades as it comes into view, once.
 *
 * Deliberately restrained, because this effect is easy to overdo and a shop
 * is not a showreel:
 *
 * - 16px of travel, not 60. Enough to read as arrival; not so much that the
 *   page appears to assemble itself.
 * - Opacity from 0 rather than a slide from the side. A horizontal slide
 *   fights the scroll direction and can widen the document.
 * - Once. `unobserve` after the first reveal, so scrolling back up does not
 *   replay it — a band that re-animates every pass is a distraction, and it
 *   makes the page feel restless rather than considered.
 * - A 90ms stagger *within* a band only, capped at six items, so a product
 *   row deals itself out but a long grid never leaves the shopper waiting on
 *   the twentieth card.
 * - `rootMargin` fires the reveal slightly before the element is on screen,
 *   so by the time it is actually looked at the motion has finished. A
 *   reveal the eye catches mid-flight reads as jank.
 *
 * Progressive enhancement: the class is added by script, so with JS off or
 * reduced motion on, every section is simply visible — the CSS that hides it
 * is scoped to `html.js-reveal`, which only this file sets.
 */

const REVEAL_SELECTOR = [
  '.shopify-section > .section',
  '.shopify-section > .editorial',
  '.cgrid > *',
  '.prail > *',
  '.pgrid > *',
].join(',');

function initReveal() {
  if (REDUCED.matches) return;
  if (!('IntersectionObserver' in window)) return;

  /* Only now is it safe to hide anything: if the script had failed to load,
   * this class would never be set and nothing would be invisible. */
  document.documentElement.classList.add('js-reveal');

  const seen = new WeakSet();

  const io = new IntersectionObserver(
    (entries, observer) => {
      /* Group by parent so the stagger counts within a band, not across the
       * whole page — otherwise the first card of the second band inherits
       * the delay of the last card of the first. */
      const byParent = new Map();

      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const el = entry.target;
        observer.unobserve(el);
        if (seen.has(el)) continue;
        seen.add(el);
        const key = el.parentElement ?? document.body;
        if (!byParent.has(key)) byParent.set(key, []);
        byParent.get(key).push(el);
      }

      for (const group of byParent.values()) {
        group.forEach((el, i) => {
          el.style.setProperty('--reveal-delay', `${Math.min(i, 5) * 90}ms`);
          el.dataset.reveal = 'in';
        });
      }
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.01 },
  );

  const targets = [...document.querySelectorAll(REVEAL_SELECTOR)];

  /* A section and the cards inside it both match the selector, so without
   * this a card would be hidden twice — once by its own rule and once by its
   * parent's — and would only appear when *both* had fired. Worse, a tall
   * parent crosses the trigger line long before its lower cards do, so the
   * parent revealed, the cards did not, and the band sat at opacity 0 with
   * its state already marked `in`. That is exactly what the first run of
   * this code did: two bands, 954px and 672px tall, stuck invisible.
   *
   * The child is the better unit — it is what staggers — so a parent that
   * contains another target is dropped. */
  const kept = targets.filter((el) => !targets.some((o) => o !== el && el.contains(o)));

  for (const el of kept) {
    /* Anything already on screen at load is shown without animating. The
     * hero must never fade in: it is the LCP element, and delaying it would
     * trade a real performance metric for an effect nobody asked for. */
    if (el.getBoundingClientRect().top < innerHeight) {
      seen.add(el);
      el.dataset.reveal = 'in';
      continue;
    }
    /* Marked pending *now*, so the CSS hides it only once the script is
     * running and only for elements this observer is actually watching. An
     * element that is never observed is never hidden. */
    el.dataset.reveal = 'out';
    io.observe(el);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initReveal, { once: true });
} else {
  initReveal();
}

window.HS = window.HS || {};
window.HS.swap = swap;
